import { useState } from "react";
import { Menu, X } from "lucide-react";

export const Nav = ({ t, lang, setLang }) => {
  const LANG_LABELS = { de: "DE", en: "EN", sh: "SR/HR", hi: "HI" };
  const FLAG_W = 14;
  const FLAG_H = 10;
  const FlagDE = (
    <svg width={FLAG_W} height={FLAG_H} viewBox="0 0 14 10" aria-hidden="true">
      <rect width="14" height="3.4" fill="#151515" />
      <rect y="3.3" width="14" height="3.4" fill="#DD0000" />
      <rect y="6.6" width="14" height="3.4" fill="#FFCC00" />
    </svg>
  );
  const FlagGB = (
    <svg width={FLAG_W} height={FLAG_H} viewBox="0 0 14 10" aria-hidden="true">
      <rect width="14" height="10" fill="#012169" />
      <path d="M0,0 L14,10 M14,0 L0,10" stroke="#FFFFFF" strokeWidth="2" />
      <path d="M0,0 L14,10 M14,0 L0,10" stroke="#C8102E" strokeWidth="0.9" />
      <path d="M7,0 V10 M0,5 H14" stroke="#FFFFFF" strokeWidth="3.2" />
      <path d="M7,0 V10 M0,5 H14" stroke="#C8102E" strokeWidth="1.8" />
    </svg>
  );
  const FlagRS = (
    <svg width={FLAG_W} height={FLAG_H} viewBox="0 0 14 10" aria-hidden="true">
      <rect width="14" height="3.4" fill="#C6363C" />
      <rect y="3.3" width="14" height="3.4" fill="#0C4076" />
      <rect y="6.6" width="14" height="3.4" fill="#FFFFFF" />
    </svg>
  );
  const FlagHR = (
    <svg width={FLAG_W} height={FLAG_H} viewBox="0 0 14 10" aria-hidden="true">
      <rect width="14" height="3.4" fill="#FF0000" />
      <rect y="3.3" width="14" height="3.4" fill="#FFFFFF" />
      <rect y="6.6" width="14" height="3.4" fill="#171796" />
    </svg>
  );
  const FlagIN = (
    <svg width={FLAG_W} height={FLAG_H} viewBox="0 0 14 10" aria-hidden="true">
      <rect width="14" height="3.4" fill="#FF9933" />
      <rect y="3.3" width="14" height="3.4" fill="#FFFFFF" />
      <rect y="6.6" width="14" height="3.4" fill="#138808" />
      <circle cx="7" cy="5" r="1.1" fill="none" stroke="#000080" strokeWidth="0.6" />
    </svg>
  );
  const LANG_FLAGS = {
    de: FlagDE,
    en: FlagGB,
    sh: (
      <span className="inline-flex gap-[2px]">
        {FlagRS}
        {FlagHR}
      </span>
    ),
    hi: FlagIN,
  };
  const [open, setOpen] = useState(false);
  const links = [
    { href: "#staerken", label: t.nav.usp, testid: "nav-link-usp" },
    { href: "#leistungen", label: t.nav.services, testid: "nav-link-services" },
    { href: "#team", label: t.nav.team, testid: "nav-link-team" },
    { href: "#referenzen", label: t.nav.references, testid: "nav-link-references" },
    { href: "#kontakt", label: t.nav.contact, testid: "nav-link-contact" },
  ];

  const langSwitcher = (
    <div
      data-testid="language-switcher"
      className="inline-flex items-center gap-1 rounded-full bg-[#0A272C]/5 p-1 border border-[#0A272C]/10 text-xs font-mono text-slate-600"
    >
      {["de", "en", "sh", "hi"].map((l) => (
        <button
          key={l}
          data-testid={`lang-switch-${l}`}
          onClick={() => setLang(l)}
          className={`flex flex-col items-center gap-0.5 px-2.5 py-1 rounded-full uppercase transition-colors duration-200 ${
            lang === l ? "bg-[#003940] text-white font-semibold" : "hover:text-[#0A272C]"
          }`}
        >
          <span data-testid={`lang-flag-${l}`} className="leading-none rounded-[2px] overflow-hidden">
            {LANG_FLAGS[l]}
          </span>
          {LANG_LABELS[l]}
        </button>
      ))}
    </div>
  );

  return (
    <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#F5FAFA]/90 border-b border-[#0A272C]/10">
      <nav className="max-w-7xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between">
        <a href="#top" data-testid="nav-logo" className="flex items-center hover:opacity-80 transition-opacity duration-200">
          <img src="/logo.png" alt="BUNDLER FM" className="h-8 sm:h-9 w-auto" />
        </a>
        <div className="hidden lg:flex items-center gap-8">
          {links.map((link) => (
            <a
              key={link.href}
              href={link.href}
              data-testid={link.testid}
              className="text-sm text-slate-600 hover:text-[#003940] transition-colors duration-200"
            >
              {link.label}
            </a>
          ))}
        </div>
        <div className="flex items-center gap-3">
          {langSwitcher}
          <a
            href="#kontakt"
            data-testid="nav-cta-button"
            className="hidden sm:inline-flex px-5 py-2.5 rounded-full bg-[#003940] hover:bg-[#002A2F] text-white font-semibold text-sm transition-all duration-200"
          >
            {t.nav.cta}
          </a>
          <button
            data-testid="mobile-menu-button"
            onClick={() => setOpen(!open)}
            className="lg:hidden p-2 text-slate-600 hover:text-[#0A272C] transition-colors"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>
      {open && (
        <div data-testid="mobile-menu" className="lg:hidden border-t border-[#0A272C]/10 bg-[#F5FAFA]/95 backdrop-blur-xl">
          <div className="px-4 py-4 flex flex-col gap-1">
            {links.map((link) => (
              <a
                key={link.href}
                href={link.href}
                data-testid={`mobile-${link.testid}`}
                onClick={() => setOpen(false)}
                className="px-3 py-3 rounded-lg text-sm text-slate-700 hover:bg-[#0A272C]/5 hover:text-[#003940] transition-colors"
              >
                {link.label}
              </a>
            ))}
            <a
              href="#kontakt"
              data-testid="mobile-nav-cta-button"
              onClick={() => setOpen(false)}
              className="mt-2 px-5 py-3 rounded-full bg-[#003940] text-white font-semibold text-sm text-center"
            >
              {t.nav.cta}
            </a>
          </div>
        </div>
      )}
    </header>
  );
};
